import { Device as CapacitorDevice } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { generateUUID } from '../utils/uuid';

const DEVICE_ID_KEY = 'deviceID';
const INVALID_DEVICE_IDS = new Set(['undefined', 'null', 'none', 'unknown', '[object object]']);

// OEM shared SMBIOS UUIDs (same list as electron/main.js + Retail + Flutter).
const JUNK_SMBIOS_UUIDS = new Set([
  '03000200-0400-0500-0006-000700080009',
  '00000000-0000-0000-0000-000000000000',
  'ffffffff-ffff-ffff-ffff-ffffffffffff',
]);

const isJunkSmbiosUuid = (value) => {
  if (typeof value !== 'string') return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  if (JUNK_SMBIOS_UUIDS.has(lower)) return true;
  const hex = lower.replace(/-/g, '');
  if (!hex) return true;
  if (/^0+$/.test(hex) || /^f+$/.test(hex)) return true;
  return false;
};

const normalizeDeviceId = (value) => {
  if (typeof value !== 'string') return null;

  const normalized = value.trim();
  if (!normalized) return null;

  if (INVALID_DEVICE_IDS.has(normalized.toLowerCase())) {
    return null;
  }

  if (isJunkSmbiosUuid(normalized)) {
    return null;
  }

  return normalized;
};

export const Device = {
  /**
   * Retrieves a stable device ID based on the platform.
   * - Electron: Hardware UUID via 'system:getMachineId'
   * - Capacitor (Mobile): Native Device ID
   * - Web: Browser Fingerprint
   * Fallback: Generated UUID
   */
  async getDeviceId() {
    // 1. Detect platform
    const isElectron = !!(window?.hardwareIPC || window?.electronBridge);
    const isNative = Capacitor.isNativePlatform();
    const isLinuxElectron = isElectron && /linux/i.test(
      `${window?.navigator?.platform || ''} ${window?.navigator?.userAgent || ''}`
    );
    const shouldUseCache = !isElectron || isLinuxElectron;

    let deviceId = null;

    // 2. Check cache first for web/mobile and Linux Electron stability
    if (shouldUseCache) {
      deviceId = normalizeDeviceId(await this.getCachedDeviceId());
      if (deviceId) return deviceId;
    }

    // 3. Platform-specific retrieval
    try {
      if (isElectron) {
        // Prefer the hardware device/volume ID on Linux because it is the identifier
        // used elsewhere for machine registration and remains stable across app starts.
        if (isLinuxElectron) {
          try {
            const hardwareBridge = (await import('@/services/hardwareBridge')).hardwareBridge;
            const pcData = await hardwareBridge.getPcData();
            deviceId = normalizeDeviceId(pcData?.VOLUMEID);
          } catch (e) {
            console.warn('[Device] Failed to get Linux hardware device ID:', e);
          }
        }

        if (!deviceId && window.electronBridge?.hardwareInvoke) {
          const machineId = await window.electronBridge.hardwareInvoke('system:getMachineId');
          deviceId = normalizeDeviceId(machineId);
        }
      } else if (isNative) {
        // Capacitor (iOS/Android): Use Native Device API
        const info = await CapacitorDevice.getId();
        deviceId = normalizeDeviceId(info?.identifier || info?.uuid);
      } else {
        // Web: Use Browser Fingerprint
        deviceId = normalizeDeviceId(await this.getFingerPrintID());
      }
    } catch (e) {
      console.warn('[Device] Failed to get platform specific ID:', e);
    }

    // Linux Electron should keep using the last known-good device ID if IPC is temporarily unavailable.
    if (!deviceId && isLinuxElectron) {
      deviceId = normalizeDeviceId(await this.getCachedDeviceId());
    }

    // 4. Final Fallback: Generated UUID
    if (!deviceId) {
      deviceId = generateUUID();
    }

    // 5. Cache result for web/mobile and Linux Electron stability
    if (shouldUseCache) {
      await this.setCachedDeviceId(deviceId);
    }

    // Also support legacy local storage key for backward compatibility if needed?
    // The previous implementation checked 'deviceRegistrationID' in localStorage.
    // Preferences uses SharedPreferences/UserDefaults on mobile, and localStorage on web.
    // So Preferences is a good replacement.

    return deviceId;
  },

  async getCachedDeviceId() {
    try {
      const { value } = await Preferences.get({ key: DEVICE_ID_KEY });
      return value;
    } catch (e) {
      return null;
    }
  },

  async setCachedDeviceId(id) {
    try {
      await Preferences.set({ key: DEVICE_ID_KEY, value: id });
    } catch (e) {
      console.error('[Device] Failed to cache device ID:', e);
    }
  },

  getFingerPrintID() {
    const fpPromise = FingerprintJS.load();
    return fpPromise
      .then((fp) => fp.get())
      .then((result) => {
        return result.visitorId;
      });
  },

  async getPCName(android) {
    if (android) return this.getDeviceId();
    try {
      if (window?.hardwareIPC || window?.electronBridge) {
        const hardwareBridge = (await import('@/services/hardwareBridge')).hardwareBridge;
        const name = await hardwareBridge.getPcName();
        return typeof name === 'string' ? name : (name?.hostname || '');
      }
    } catch (e) {
      console.warn('Failed to get PC name via Electron IPC:', e);
    }
    // Web fallback: derive a pseudo name
    try {
      const nav = navigator;
      return `${nav.platform || 'Web'}-${(await this.getDeviceId()).slice(0, 8)}`;
    } catch (e) {
      return 'Web-Unknown';
    }
  },

  async getPCIP() {
    try {
      if (window?.hardwareIPC || window?.electronBridge) {
        const hardwareBridge = (await import('@/services/hardwareBridge')).hardwareBridge;
        const ip = await hardwareBridge.getPcIP();
        return ip || 'localhost';
      }
    } catch (e) {
      console.warn('Failed to get PC IP via Electron IPC:', e);
    }
    // Web fallback: return localhost
    return 'localhost';
  },

  /**
   * System serial (VOLUMEID) for Electron; empty on web/mobile when unavailable.
   * Matches validatePCRegistration volumeId (serial, else MAC).
   */
  async getDeviceSerial() {
    try {
      if (!(window?.hardwareIPC || window?.electronBridge)) return '';
      const hardwareBridge = (await import('@/services/hardwareBridge')).hardwareBridge;
      const data = await hardwareBridge.getPcData();
      if (!data || data.ok === false) return '';
      const serial = data.VOLUMEID;
      if (typeof serial !== 'string') return '';
      const trimmed = serial.trim();
      if (!trimmed || INVALID_DEVICE_IDS.has(trimmed.toLowerCase())) return '';
      return trimmed;
    } catch (e) {
      console.warn('[Device] Failed to get device serial:', e);
      return '';
    }
  },

  async getLoginDeviceInfo() {
    const [deviceId, deviceName, deviceSerial] = await Promise.all([
      this.getDeviceId().catch(() => ''),
      this.getPCName(false).catch(() => ''),
      this.getDeviceSerial().catch(() => ''),
    ]);
    return {
      deviceId: deviceId || '',
      deviceName: deviceName || '',
      deviceSerial: deviceSerial || '',
    };
  },

  // Keep requestPersistentStorage for compatibility if used elsewhere, though not used in new getDeviceId
  async requestPersistentStorage() {
    if (navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().then((persistent) => {
        // consistent
      });
    }
  },
};
